import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import SpeakerCard from "../components/SpeakerCard";
import { speakersAPI } from "../services/api";
import "./Speakers.css";

const Speakers = () => {
  const [speakers, setSpeakers] = useState([]);
  const [filteredSpeakers, setFilteredSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Accordion state
  const [expandedId, setExpandedId] = useState(null);
  const [detailedSpeakers, setDetailedSpeakers] = useState({}); // Cache for full details
  const [loadingDetails, setLoadingDetails] = useState(false);
  const navigate = useNavigate();
  const { eventId } = useParams();

  useEffect(() => {
    fetchSpeakers();
  }, [eventId]);

  useEffect(() => {
    filterSpeakers();
  }, [speakers, searchQuery]);

  const fetchSpeakers = async () => {
    setLoading(true);
    try {
      const response = await speakersAPI.getAll(eventId);
      setSpeakers(response.data);
    } catch (error) {
      console.error("Failed to fetch speakers:", error);
      setSpeakers(getDemoSpeakers());
    } finally {
      setLoading(false);
    }
  };

  const filterSpeakers = () => {
    if (!searchQuery) {
      setFilteredSpeakers(speakers);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = speakers.filter(
      (speaker) =>
        speaker.name.toLowerCase().includes(query) ||
        speaker.title?.toLowerCase().includes(query) ||
        speaker.organization?.toLowerCase().includes(query),
    );
    setFilteredSpeakers(filtered);
  };

  const toggleSpeaker = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(id);

    // If details not cached, fetch them
    if (!detailedSpeakers[id]) {
      setLoadingDetails(true);
      try {
        const response = await speakersAPI.getById(id);
        setDetailedSpeakers((prev) => ({ ...prev, [id]: response.data }));
      } catch (error) {
        console.error("Failed to fetch detailed speaker:", error);
        // Fallback to basic info if fetch fails, effectively showing what we have
        setDetailedSpeakers((prev) => ({
          ...prev,
          [id]: speakers.find((s) => s.id === id),
        }));
      } finally {
        setLoadingDetails(false);
      }
    }
  };

  const getDemoSpeakers = () => [
    {
      id: "1",
      name: "Dr. Anita Sharma",
      title: "CEO",
      organization: "Education Innovation",
      sessions_count: 2,
    },
    {
      id: "2",
      name: "Rajesh Kumar",
      title: "Design Director",
      organization: "Creative Labs",
      sessions_count: 1,
    },
    {
      id: "3",
      name: "Priya Menon",
      title: "Social Entrepreneur",
      organization: "Impact Ventures",
      sessions_count: 3,
    },
    {
      id: "4",
      name: "Vikram Singh",
      title: "Tech Leader",
      organization: "Digital India",
      sessions_count: 2,
    },
    {
      id: "5",
      name: "Dr. Meera Patel",
      title: "Research Director",
      organization: "Health Foundation",
      sessions_count: 1,
    },
  ];

  // Helper for formatting dates in sessions list
  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="speakers-page">
      <div className="search-bar">
        <Search size={20} className="search-icon" />
        <input
          type="text"
          placeholder="Search speakers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="search-clear" onClick={() => setSearchQuery("")}>
            <X size={18} />
          </button>
        )}
      </div>

      <div className="results-count">
        {filteredSpeakers.length} speaker
        {filteredSpeakers.length !== 1 ? "s" : ""}
      </div>

      <div className="speakers-list">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="speaker-skeleton">
              <div
                className="skeleton"
                style={{ width: 64, height: 64, borderRadius: 12 }}
              />
              <div style={{ flex: 1 }}>
                <div
                  className="skeleton"
                  style={{ height: 20, width: "60%", marginBottom: 8 }}
                />
                <div
                  className="skeleton"
                  style={{ height: 16, width: "80%" }}
                />
              </div>
            </div>
          ))
        ) : filteredSpeakers.length > 0 ? (
          filteredSpeakers.map((speaker) => {
            const isExpanded = expandedId === speaker.id;
            const fullDetails = detailedSpeakers[speaker.id] || speaker;

            return (
              <div key={speaker.id} className="speaker-accordion-item">
                <SpeakerCard
                  speaker={speaker}
                  onClick={() => toggleSpeaker(speaker.id)}
                  isExpanded={isExpanded}
                />

                {isExpanded && (
                  <div className="speaker-accordion-content">
                    {loadingDetails && !detailedSpeakers[speaker.id] ? (
                      <div className="speaker-accordion-loader">
                        <div className="spinner"></div>
                      </div>
                    ) : (
                      <>
                        <div className="speaker-social-row">
                          {fullDetails.email && (
                            <div className="social-pill">
                              <a href={`mailto:${fullDetails.email}`}>Email</a>
                            </div>
                          )}
                          {fullDetails.linkedin && (
                            <div className="social-pill">
                              <a
                                href={fullDetails.linkedin}
                                target="_blank"
                                rel="noreferrer"
                              >
                                LinkedIn
                              </a>
                            </div>
                          )}
                          {fullDetails.twitter && (
                            <div className="social-pill">
                              <a
                                href={fullDetails.twitter}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Twitter
                              </a>
                            </div>
                          )}
                          {fullDetails.website && (
                            <div className="social-pill">
                              <a
                                href={fullDetails.website}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Website
                              </a>
                            </div>
                          )}
                        </div>

                        <div className="speaker-bio-text">
                          {fullDetails.bio ? (
                            fullDetails.bio
                          ) : (
                            <span
                              className="text-muted"
                              style={{ fontStyle: "italic" }}
                            >
                              Biography not available.
                            </span>
                          )}
                        </div>

                        {fullDetails.sessions &&
                          fullDetails.sessions.length > 0 && (
                            <div className="speaker-sessions-list">
                              <h4>Sessions</h4>
                              {fullDetails.sessions.map((session) => (
                                <div
                                  key={session.id}
                                  className="session-mini-item"
                                  onClick={() =>
                                    navigate(
                                      eventId
                                        ? `/event/${eventId}/session/${session.id}`
                                        : `/session/${session.id}`,
                                    )
                                  }
                                >
                                  <span className="session-mini-time">
                                    {formatDate(session.start_time)},{" "}
                                    {formatTime(session.start_time)}
                                  </span>
                                  <span className="session-mini-title">
                                    {session.title}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Search size={32} />
            </div>
            <h3>No speakers found</h3>
            <p>Try a different search term</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Speakers;
